import { useState } from 'react'
import { Close, Edit } from '@pay-com/icons'
import { Button, IconButton } from '@pay-com/components'
import { useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ICustomFieldComponentProps } from 'types'
import { Website } from 'pages/Onboarding'
import { GeneralInformationForm } from 'pages/Onboarding/OnboardingRegion/UsRegion/UsRegionStepsConfigs/GeneralInformation/general-information-schema'
import { ErrorMessage } from 'components/ErrorMessage'
import { WebsiteManagementModal } from './WebsiteManagementModal'

type ModalState =
  | { open: false }
  | { open: true; mode: 'add' }
  | { open: true; mode: 'edit'; website: Website }

type WebsiteManagementFieldProps = ICustomFieldComponentProps<
  GeneralInformationForm,
  'websites'
>

export const WebsiteManagementField = ({
  name,
  label
}: WebsiteManagementFieldProps) => {
  const { t } = useTranslation()
  const { control, trigger } = useFormContext<GeneralInformationForm>()
  const { field, fieldState } = useController({ name, control })

  const websites = field.value ?? []

  const [modal, setModal] = useState<ModalState>({ open: false })

  const openAdd = () => setModal({ open: true, mode: 'add' })
  const openEdit = (w: Website) =>
    setModal({ open: true, mode: 'edit', website: w })
  const close = () => setModal({ open: false })

  const editedDomain =
    modal.open && modal.mode === 'edit' ? modal.website.domain : undefined

  const onConfirm = (website: Website) => {
    if (modal.open && modal.mode === 'edit') {
      field.onChange(
        websites.map(w => (w.domain === modal.website.domain ? website : w))
      )
    } else {
      field.onChange([...websites, website])
    }
    close()
    trigger(name)
  }

  const onRemove = (website: Website) => {
    field.onChange(websites.filter(w => w.domain !== website.domain))
    trigger(name)
  }

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <span className="text-sm font-medium text-gray-700">{label}</span>
      )}
      <WebsiteManagementModal
        isOpen={modal.open}
        defaultValues={
          modal.open && modal.mode === 'edit' ? modal.website : undefined
        }
        existingWebsites={websites.filter(w => w.domain !== editedDomain)}
        isEdit={modal.open && modal.mode === 'edit'}
        onConfirm={onConfirm}
        onDismiss={close}
      />
      <Button
        type="button"
        variant="secondary"
        onClick={openAdd}
        className="w-36"
      >
        {t('generalInformation.fields.websites.addButton')}
      </Button>
      <div className="flex flex-col gap-2">
        {websites.map(website => (
          <div
            key={website.domain}
            className="flex items-center justify-between rounded-lg bg-gray-50 p-3 md:p-6"
          >
            <div className="flex flex-col">
              <span className="text-sm font-medium">{website.domain}</span>
              {website.login && (
                <span className="text-xs text-gray-500">
                  Login: {website.login} | Password: ******
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <IconButton
                variant="secondary"
                type="button"
                title="edit row"
                testId="button-edit-card"
                onClick={() => openEdit(website)}
              >
                <Edit color="primary" width={20} />
              </IconButton>
              <IconButton
                type="button"
                variant="secondary"
                title="delete row"
                testId="button-delate-card"
                onClick={() => onRemove(website)}
              >
                <Close color="primary" width={20} />
              </IconButton>
            </div>
          </div>
        ))}
      </div>
      {fieldState.error && (
        <ErrorMessage errorMessage={fieldState?.error?.message} />
      )}
    </div>
  )
}
